import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SippApi } from '../../../../services/SippExpress'
import { AppDispatch } from '../../../../store/store'
import { SnackbarActions } from '../../../../store/snackbar'
import { UserSelector } from '../../../../store/User'
import {
  ProductType,
  productTypeToWaitlistAttribute,
} from '../../../../utils/productTypeWaitlist'

interface Step3WaitlistBranchProps {
  equipmentType: string
  productType: ProductType
}

export const Step3WaitlistBranch = ({
  equipmentType,
  productType,
}: Step3WaitlistBranchProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const user = useSelector(UserSelector.user)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleNotify = async () => {
    if (!user?._id) {
      dispatch(
        SnackbarActions.showSnackbar({
          message: 'Please verify your email first.',
          options: { variant: 'error' },
        })
      )
      return
    }
    setSubmitting(true)
    try {
      await SippApi.create.waitlistSignup(user._id, {
        attribute: productTypeToWaitlistAttribute(productType),
      })
      setSubmitted(true)
      dispatch(
        SnackbarActions.showSnackbar({
          message: "You're on the list — we'll be in touch.",
          options: { variant: 'success' },
        })
      )
    } catch {
      dispatch(
        SnackbarActions.showSnackbar({
          message: "Couldn't sign you up right now. Please try again.",
          options: { variant: 'error' },
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  // SIPP-837: success UI (the green checkmark + "You're on the list" header)
  // is now gated behind `submitted`. The pre-submit state shows the waitlist
  // pitch + Notify button; we only flip to the confirmation state after the
  // API call resolves.
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {submitted ? (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Typography variant="h5">You&apos;re on the list.</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            We&apos;ll email you the moment {equipmentType} fulfillment is ready, and alert you if your water data shifts in the meantime.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Join the {equipmentType} waitlist
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We don&apos;t support {equipmentType} fulfillment yet — but you&apos;re first to know when we do. We&apos;ll also alert you if your water data shifts in the meantime.
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleNotify}
            disabled={submitting}
          >
            {submitting ? 'Adding you to the list…' : 'Notify me when it’s ready'}
          </Button>
        </>
      )}
    </Box>
  )
}
